import React, { useEffect, useState, useCallback, useRef } from "react";
import { HiArrowSmLeft } from "react-icons/hi";
import { TrashIcon } from "@heroicons/react/24/outline";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import FileUpload from "../common/FileUpload";
import ShiftBookingDrawer from "./ShiftBookingDrawer";
import newloader from "../../icons/new_loader.gif";

const FormField = React.memo(
  ({ label, required, error, children, classApplied = "" }) => (
    <div className={`space-y-2 ${classApplied}`}>
      <label className="block text-[15px] font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  ),
);

FormField.displayName = "FormField";

const RoomTypeDrawer = ({ isOpen, onClose,onSaved, roomId = null }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [roomForm, setRoomForm] = useState({
    room_name: "",
    description: "",
    room_category: "",
    environment_type: "Cage Free",
    camera: "Yes",
    kennel_size: "",
    pet_type: [],
    occupancy_limit: 1,
    images: [],
    set_default_numbering: "No",
  });

  const [petTypes, setPetTypes] = useState([]);
  const [selectedPetTypes, setSelectedPetTypes] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [loadingPetTypes, setLoadingPetTypes] = useState(false);
  const dropdownRef = useRef(null);
  const isInteractingRef = useRef(false);

  const [roomBoxes, setRoomBoxes] = useState([{ id: 1, roomNumber: "" }]);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [originalRoomData, setOriginalRoomData] = useState(null);
  const [bookedRooms, setBookedRooms] = useState(new Set());
  const [showShiftDrawer, setShowShiftDrawer] = useState(false);
  const [pendingOccupancyChange, setPendingOccupancyChange] = useState(null);
  const [removedRoomsList, setRemovedRoomsList] = useState([]);
  const [availableRoomsList, setAvailableRoomsList] = useState([]);
  const [isCheckingBookings, setIsCheckingBookings] = useState(false);

  const shiftConfirmedRef = useRef(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  const loadPetTypes = async () => {
    setLoadingPetTypes(true);
    try {
      const res = await httpService.get("/vendors/getPetTypes?isEnabled=true");
      const list = res.data?.data ?? res.data ?? [];
      const transformedList = list.map((petType) => ({
        pet_type_id: petType.id || petType.pet_type_id,
        name: petType.name,
      }));
      setPetTypes(transformedList);
    } catch (err) {
      console.error("Error loading pet types:", err);
      toast.error("Failed to load pet types.");
    } finally {
      setLoadingPetTypes(false);
    }
  };

  const validate = useCallback(
    (field, value) => {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        switch (field) {
          case "room_name":
            if (!value?.trim()) newErrors.room_name = "Room Name is required";
            else delete newErrors.room_name;
            break;
          case "pet_type":
            if (selectedPetTypes.length === 0)
              newErrors.pet_type = "Pet Type is required";
            else delete newErrors.pet_type;
            break;
        }
        return newErrors;
      });
    },
    [selectedPetTypes.length],
  );

  const getRoomDetails = useCallback(async (id) => {
    setLoading(true);
    try {
      const response = await httpService.get(`/room/getRoom/${id}`);
      const data = response?.data || {};

      setOriginalRoomData(data);
      

      const backendDefaultFlag =
        data.set_default_numbering ?? data.default_numbering ?? "No";

      setRoomForm({
        room_name: data.room_name ?? "",
        room_category: data.room_category ?? "",
        description: data.description ?? "",
        environment_type: data.environment_type ?? "Cage Free",
        camera: data.camera ?? "Yes",
        pet_type: data.pet_type ?? [],
        kennel_size: data.kennel_size ?? "",
        occupancy_limit: data.occupancy_limit ?? 1,
        images: data.images ?? [],
        set_default_numbering:
          backendDefaultFlag === true ? "Yes" : backendDefaultFlag || "No",
      });

      if (data.pet_type && Array.isArray(data.pet_type)) {
        const preSelected = data.pet_type.map((pet) => ({
          pet_type_id: pet.id || pet._id,
          name: pet.name,
        }));
        setSelectedPetTypes(preSelected);
      }

      if (backendDefaultFlag === "No") {
        if (Array.isArray(data.room_numbers) && data.room_numbers.length > 0) {
          setRoomBoxes(
            data.room_numbers.map((r, i) => ({
              id: i + 1,
              roomNumber: r.room_number || "",
            })),
          );
        } else {
          const count = parseInt(data.occupancy_limit) || 1;
          setRoomBoxes(
            Array.from({ length: count }, (_, i) => ({
              id: i + 1,
              roomNumber: "",
            })),
          );
        }
      } else {
        const count = parseInt(data.occupancy_limit) || 1;
        const start = 101;
        const defaultNumbers = Array.from({ length: count }, (_, i) => ({
          id: i + 1,
          roomNumber: (start + i).toString(),
        }));
        setRoomBoxes(defaultNumbers);
      }
    } catch {
      toast.error("Failed to fetch Room details");
    } finally {
      setLoading(false);
    }
  }, []);

  const getPetTypeId = (pt) => pt.pet_type_id || pt.id || pt._id;

  const resetForm = () => {
    setRoomForm({
      room_name: "",
      description: "",
      room_category: "",
      environment_type: "Cage Free",
      camera: "Yes",
      kennel_size: "",
      pet_type: [],
      occupancy_limit: 1,
      images: [],
      set_default_numbering: "No",
    });
    setSelectedPetTypes([]);
    setRoomBoxes([{ id: 1, roomNumber: "" }]);
    setErrors({});
    setTouched({});
    shiftConfirmedRef.current = false;
  };

  useEffect(() => {
    if (isOpen) {
      loadPetTypes();
      if (roomId) {
        getRoomDetails(roomId);
      } else {
        resetForm();
      }
    }
  }, [roomId, isOpen]);

  useEffect(() => {
    const isAll =
      petTypes.length > 0 && selectedPetTypes.length === petTypes.length;
    if (isAll !== selectAll) {
      setSelectAll(isAll);
    }
  }, [selectedPetTypes, petTypes, selectAll]);

  const canDecreaseOccupancy = useCallback(
    async (roomId, newOccupancyLimit) => {
      

      const totalOccupancy =
        originalRoomData?.occupancy_limit || roomForm.occupancy_limit;

      // Case 1: Increasing or same — always allow
      if (newOccupancyLimit >= totalOccupancy) {
        console.log(" Increasing or same occupancy — no validation needed");
        return true;
      }


      try {
        setIsCheckingBookings(true);

        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 90);
        const toDate = new Date(today);
        toDate.setDate(today.getDate() + 90);

        const payload = {
          dateType: "custom",
          fromDate: fromDate.toISOString().split("T")[0],
          toDate: toDate.toISOString().split("T")[0],
        };

        const response = await httpService.post(
          "/vendorbooking/getVendorBookings",
          payload,
        );

        // Handle response properly
        let bookings = [];
        if (Array.isArray(response.data)) {
          bookings = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          bookings = response.data.data;
        } else if (
          response.data?.bookings &&
          Array.isArray(response.data.bookings)
        ) {
          bookings = response.data.bookings;
        } else {
          bookings = [];
        }

        const currentRoomNumbers = originalRoomData?.room_numbers || [];
        const currentRoomNumberStrings = currentRoomNumbers.map(
          (r) => r.room_number,
        );

        const activeStatuses = ["confirmed", "active", "pending", "checked_in"];
        const currentRoomTypeId = originalRoomData?._id || roomId;

        const roomBookings = bookings.filter((booking) => {
          const match =
            booking.room_number &&
            currentRoomNumberStrings.includes(booking.room_number) &&
            booking.room_type_id === currentRoomTypeId &&
            activeStatuses.includes(booking.status);

          if (match) {
            console.log(
              `  Found booking: Room ${booking.room_number} | Pet: ${booking.pet_name} | Status: ${booking.status} | ID: ${booking._id}`,
            );
          }

          return match;
        });

        // Get all unique booked room numbers
        const bookedRoomNumbers = new Map(); // Use Map to store booking IDs per room
        roomBookings.forEach((booking) => {
          if (booking.room_number) {
            if (!bookedRoomNumbers.has(booking.room_number)) {
              bookedRoomNumbers.set(booking.room_number, []);
            }
            bookedRoomNumbers.get(booking.room_number).push({
              id: booking._id,
              pet_name: booking.pet_name,
              start_date: booking.start_date,
              end_date: booking.end_date,
            });
          }
        });
        for (const [roomNum, bookings] of bookedRoomNumbers.entries()) {
          bookings.forEach((b) => {
            console.log(`    - Booking ID: ${b.id} | Pet: ${b.pet_name}`);
          });
        }

        const bookedCount = bookedRoomNumbers.size;

        // Case 2: Trying to go below total booked count — block entirely
        if (newOccupancyLimit < bookedCount) {
          toast.error(
            `Cannot reduce occupancy. ${bookedCount} room(s) are currently booked.`,
          );
          return false;
        }

        // Determine kept vs removed rooms
        const keptRooms = currentRoomNumbers.slice(0, newOccupancyLimit);
        const removedRooms = currentRoomNumbers.slice(newOccupancyLimit);

        const keptRoomNumbers = keptRooms.map((r) => r.room_number);
        const removedRoomNumbers = removedRooms.map((r) => r.room_number);

        // Find bookings that fall in rooms being removed
        const bookingsToShift = roomBookings.filter((booking) =>
          removedRoomNumbers.includes(booking.room_number),
        );

        bookingsToShift.forEach((b, idx) => {
          console.log(
            `  ${idx + 1}. Room ${b.room_number} → ${b.pet_name} (Booking ID: ${b._id})`,
          );
        });

        // Case 3: Some removed rooms have bookings
        if (bookingsToShift.length > 0) {
          // Check which kept rooms are FREE (no active booking)
          const freeKeptRooms = keptRooms.filter(
            (r) => !bookedRoomNumbers.has(r.room_number),
          );
          const bookedKeptRooms = keptRooms.filter((r) =>
            bookedRoomNumbers.has(r.room_number),
          );

          const freeKeptRoomNumbers = freeKeptRooms.map((r) => r.room_number);
          const bookedKeptRoomNumbers = bookedKeptRooms.map(
            (r) => r.room_number,
          );

          // ✅ If no free kept rooms exist — all kept rooms are also booked, block entirely
          if (freeKeptRooms.length === 0) {
            toast.error(
              "Cannot reduce occupancy because room is  fully booked. ",
            );
            return false;
          }

          if (bookingsToShift.length > freeKeptRooms.length) {
            toast.error(
              `Cannot reduce occupancy. ${bookingsToShift.length} booking(s) need shifting but only ${freeKeptRooms.length} free room(s) available.`,
            );
            return false;
          }

          setRemovedRoomsList(removedRooms);
          setAvailableRoomsList(freeKeptRooms);
          setPendingOccupancyChange(newOccupancyLimit);
          setShowShiftDrawer(true);
          return false; // ← IMPORTANT: Return false to indicate validation failed (because drawer opened)
        }

        // Check if any booked rooms would be removed without an available slot
        const bookedRemovedRooms = removedRoomNumbers.filter((roomNum) =>
          bookedRoomNumbers.has(roomNum),
        );

        if (bookedRemovedRooms.length > 0) {
          toast.error(
            `Cannot reduce occupancy. The following room(s) are booked and would be removed: ${bookedRemovedRooms.join(", ")}`,
          );
          return false;
        }

        return true;
      } catch (err) {
        console.error(" Error checking bookings:", err);
        console.error("Error response:", err.response?.data);
        toast.error("Failed to verify room bookings");
        return false;
      } finally {
        setIsCheckingBookings(false);
      }
    },
    [originalRoomData, roomForm.occupancy_limit],
  );

  useEffect(() => {
    if (originalRoomData) {
    
    }
  }, [originalRoomData]);

  const updateRoomForm = (field, value) => {
    setRoomForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "occupancy_limit" && next.set_default_numbering === "No") {
        const count = parseInt(value) || 1;
        setRoomBoxes((prevBoxes) => {
          const newBoxes = [...prevBoxes];
          if (count > newBoxes.length) {
            for (let i = newBoxes.length; i < count; i++) {
              newBoxes.push({ id: i + 1, roomNumber: "" });
            }
          } else {
            newBoxes.length = count;
          }
          return newBoxes;
        });
      }
      return next;
    });
    if (field !== "occupancy_limit") {
      validate(field, value);
    }
  };

  const handleChange = useCallback(
    (field, value) => {
      setRoomForm((prev) => {
        const next = { ...prev, [field]: value };

        if (
          field === "occupancy_limit" &&
          next.set_default_numbering === "No"
        ) {
          const count = parseInt(value) || 1;
          setRoomBoxes((prevBoxes) => {
            const newBoxes = [...prevBoxes];
            if (count > newBoxes.length) {
              for (let i = newBoxes.length; i < count; i++) {
                newBoxes.push({ id: i + 1, roomNumber: "" });
              }
            } else if (count < newBoxes.length) {
              newBoxes.length = count;
            }
            return newBoxes;
          });
        }

        if (field === "set_default_numbering") {
          if (value === "Yes") {
            setRoomBoxes([]);
          } else {
            const count = parseInt(prev.occupancy_limit) || 1;
            setRoomBoxes((prevBoxes) => {
              const newBoxes = [];
              for (let i = 0; i < count; i++) {
                newBoxes.push({
                  id: i + 1,
                  roomNumber: prevBoxes[i]?.roomNumber || "",
                });
              }
              return newBoxes;
            });
          }
        }

        return next;
      });

      if (field !== "occupancy_limit") {
        validate(field, value);
      }
    },
    [validate],
  );

  const togglePetType = (petType) => {
    setSelectedPetTypes((prev) => {
      const exists = prev.some((p) => p.pet_type_id === petType.pet_type_id);
      return exists
        ? prev.filter((p) => p.pet_type_id !== petType.pet_type_id)
        : [...prev, petType];
    });
  };

  const removePetType = (id) => {
    setSelectedPetTypes((prev) => prev.filter((p) => p.pet_type_id !== id));
  };

  const toggleSelectAll = () => {
    setSelectedPetTypes(selectAll ? [] : [...petTypes]);
  };

  const handleBoxChange = (id, value) => {
    setRoomBoxes((prev) =>
      prev.map((box) => (box.id === id ? { ...box, roomNumber: value } : box)),
    );
  };

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validate(field, roomForm[field]);
    },
    [validate, roomForm.room_name, roomForm.pet_type],
  );

  useEffect(() => {
    if (!dropdownOpen) {
      isInteractingRef.current = false;
      validate("pet_type");
    }
  }, [dropdownOpen]);
  const executeSave = async (occupancyOverride) => {
  const effectiveOccupancy =
    occupancyOverride ?? parseInt(roomForm.occupancy_limit) ?? 0;



  // Build the final room numbers based on occupancy
  let finalRoomNumbers = null;
  if (roomForm.set_default_numbering === "No") {
    // Get current room numbers from roomBoxes
    const currentRoomNumbers = roomBoxes.map((box) => box.roomNumber);
    // Take only the first 'effectiveOccupancy' rooms
    finalRoomNumbers = currentRoomNumbers.slice(0, effectiveOccupancy);
  }

  const payload = {
    ...roomForm,
    occupancy_limit: effectiveOccupancy,
    pet_type: selectedPetTypes.map((pt) => pt.pet_type_id),
    room_numbers: finalRoomNumbers,
    is_default_room: roomForm.set_default_numbering,
  };


    try {
      setIsSaving(true);
      let savedRoom = null;
      if (roomId) {
        const res = await httpService.put(`/room/updateRoom/${roomId}`, payload);
        savedRoom = res?.data?.data || res?.data || null;
        toast.success("Room updated successfully!");
      } else {
        const res = await httpService.post("/room/addRoom", payload);
        savedRoom = res?.data?.data || res?.data || null;
        toast.success("Room added successfully!");
      }
      resetForm();
      if (onSaved) {
        onSaved(savedRoom);
      } else {
        onClose();
      }
  } catch (err) {
    console.error("Save error:", err);
    console.error("Error response:", err.response?.data);
    toast.error(err.response?.data?.message || "Failed to add/update Room");
  } finally {
    setIsSaving(false);
  }
};

  const handleSaveRoom = async () => {

    // --- Basic validations ---
    const localErrors = {};
    if (!roomForm.room_name.trim())
      localErrors.room_name = "Room Name is required";
    if (selectedPetTypes.length === 0)
      localErrors.pet_type = "Pet Type is required";

    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      toast.warn("Please fill all required fields");
      return;
    }

    const newOccupancy = parseInt(roomForm.occupancy_limit) || 0;
    if (newOccupancy < 1) {
      toast.error("Occupancy must be at least 1");
      return;
    }

    if (roomForm.set_default_numbering === "No") {
      const emptyBoxes = roomBoxes.filter(
        (b) => !b.roomNumber || !b.roomNumber.trim(),
      );
      if (emptyBoxes.length > 0) {
        toast.warn("Please fill all room numbers before saving");
        return;
      }
    }

    // Only validate occupancy decrease for existing rooms AND if shift hasn't already been confirmed
    if (roomId && !shiftConfirmedRef.current) {
      const currentOccupancy = originalRoomData?.occupancy_limit || 0;
     

      if (newOccupancy < currentOccupancy) {
        const allowed = await canDecreaseOccupancy(roomId, newOccupancy);

        // IMPORTANT: If canDecreaseOccupancy opened the shift drawer, it returns false
        // In this case, we should NOT show any error and just return
        // The save will be triggered again from handleShiftConfirmed
        if (showShiftDrawer) {
          console.log("Shift drawer opened, waiting for user confirmation...");
          return; //  Just return, don't show error
        }

        if (!allowed) {
          console.log("Occupancy decrease not allowed, save aborted");
          // toast.error(
          //   "Cannot reduce occupancy. Please check existing bookings.",
          // );
          return;
        }
      }
    }

    // Reset the flag after using it
    shiftConfirmedRef.current = false;

    await executeSave();
  };

  const handleDeleteRoom = async () => {
    if (!roomId) return;

    const result = await Swal.fire({
      title: "Delete room?",
      text: "This action cannot be undone. The room and its data will be removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);
      await httpService.delete(`/room/deleteRoom/${roomId}`);
      toast.success("Room deleted successfully", { position: "top-center" });
      if (onSaved) {
        onSaved({ deleted: true, roomId });
      }
      onClose();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Failed to delete Room");
    } finally {
      setIsSaving(false);
    }
  };

  const handleShiftConfirmed = async () => {

    // IMPORTANT: Close the drawer first
    setShowShiftDrawer(false);

    const confirmedOccupancy = pendingOccupancyChange;

    // Clear pending data
    setPendingOccupancyChange(null);
    setRemovedRoomsList([]);
    setAvailableRoomsList([]);

    // Apply occupancy change to form
    if (confirmedOccupancy) {
      // Update form state
      setRoomForm((prev) => ({
        ...prev,
        occupancy_limit: confirmedOccupancy,
      }));

      // Update room boxes if manual numbering
      if (roomForm.set_default_numbering === "No") {
        setRoomBoxes((prev) => {
          const trimmed = prev.slice(0, confirmedOccupancy);
          return trimmed;
        });
      }
    }

    // Mark shift as confirmed so handleSaveRoom skips validation
    shiftConfirmedRef.current = true;

    // Small delay to ensure state updates are processed
    setTimeout(async () => {
      // Call executeSave directly with the confirmed occupancy
      await executeSave(confirmedOccupancy);
      // Reset the flag after save
      shiftConfirmedRef.current = false;
    }, 100);
  };

  // Show ShiftBookingDrawer on top of everything when needed
  if (showShiftDrawer) {
    return (
      <ShiftBookingDrawer
        isOpen={showShiftDrawer}
        onClose={() => {
          setShowShiftDrawer(false);
          setPendingOccupancyChange(null);
          setRemovedRoomsList([]);
          setAvailableRoomsList([]);
        }}
        onConfirm={handleShiftConfirmed}
        roomId={roomId}
        removedRooms={removedRoomsList}
        availableRooms={availableRoomsList}
      />
    );
  }

  if (!isOpen) return null;

  return (
      <div
        className={`fixed inset-0 z-[10010] transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full lg:w-2/5 bg-white shadow-xl transform transition-transform duration-300 ease-out z-[10010] ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="flex items-center justify-between gap-2 p-4 border-b shadow-sm">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-full hover:bg-gray-100 p-1 transition-colors"
            >
              <HiArrowSmLeft className="w-7 h-7" />
            </button>
            <h2 className="text-xl font-semibold">
              {roomId ? "Edit Room" : "Add Room"}
            </h2>
          </div>

          {roomId && (
            <button
              type="button"
              onClick={handleDeleteRoom}
              disabled={isSaving}
              className="flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-2 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
              title="Delete room"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6 relative">
          {(isSaving || isCheckingBookings) && (
            <div className="absolute inset-0 z-[998] backdrop-blur-sm bg-white/40" />
          )}

          {isCheckingBookings && (
            <div className="absolute inset-0 z-[999] flex flex-col items-center justify-center pointer-events-none">
              <div className="bg-white px-6 py-5 rounded-lg shadow-lg flex flex-col items-center">
                <img src={newloader} alt="Loading..." className="w-16 h-16" />
                <p className="text-gray-600 mt-3 font-medium">
                  Checking room bookings...
                </p>
                <p className="text-gray-400 text-sm mt-1">Please wait</p>
              </div>
            </div>
          )}
          {loading ? (
            <LoaderSpiner text="Loading . . ." />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Room Name"
                  required
                  error={touched.room_name && errors.room_name}
                  classApplied="col-span-1 md:col-span-2"
                >
                  <input
                    type="text"
                    value={roomForm.room_name}
                    onChange={(e) => handleChange("room_name", e.target.value)}
                    onBlur={() => handleBlur("room_name")}
                    placeholder="Enter Room Name"
                    className="border border-gray-300 rounded-md w-full p-2 focus:ring-1 focus:ring-blue-500"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Room Category">
                  <select
                    value={roomForm.room_category}
                    onChange={(e) =>
                      handleChange("room_category", e.target.value)
                    }
                    className="border border-gray-300 rounded-md w-full p-2 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="A/C">A/C</option>
                    <option value="Non-A/C">Non-A/C</option>
                    <option value="Cooler">Cooler</option>
                    <option value="Fan">Fan</option>
                    <option value="Open to Air">Open to Air</option>
                  </select>
                </FormField>
                {!(isSaving || isCheckingBookings) && (
                  <FormField
                    label="Pet Type"
                    required
                    error={touched.pet_type && errors.pet_type}
                    classApplied="relative z-[60]"
                  >
                    <div className="relative" ref={dropdownRef}>
                      <div
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white min-h-[42px] flex flex-wrap items-center gap-2 cursor-pointer"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          isInteractingRef.current = true;
                          setDropdownOpen((prev) => !prev);
                        }}
                      >
                        {selectedPetTypes.length === 0 ? (
                          <span className="text-gray-500">
                            Select Pet Types
                          </span>
                        ) : (
                          selectedPetTypes.map((pt) => (
                            <span
                              key={pt.pet_type_id}
                              className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full"
                            >
                              {pt.name}
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  removePetType(pt.pet_type_id);
                                }}
                                className="ml-1 hover:bg-green-200 rounded-full w-5 h-5 flex items-center justify-center"
                              >
                                ×
                              </button>
                            </span>
                          ))
                        )}
                        <svg
                          className={`ml-auto w-5 h-5 transition-transform ${
                            dropdownOpen ? "rotate-180" : ""
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>

                      {dropdownOpen && (
                        <div
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto"
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <div
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              toggleSelectAll();
                            }}
                          >
                            <span className="font-medium">
                              {selectAll ? "Unselect All" : "Select All"}
                            </span>
                          </div>

                          {loadingPetTypes ? (
                            <div className="px-4 py-3 text-gray-500">
                              Loading...
                            </div>
                          ) : petTypes.length === 0 ? (
                            <div className="px-4 py-3 text-gray-500">
                              No pet types found
                            </div>
                          ) : (
                            petTypes.map((pt) => {
                              const isSelected = selectedPetTypes.some(
                                (s) => s.pet_type_id === pt.pet_type_id,
                              );
                              return (
                                <div
                                  key={pt.pet_type_id}
                                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    togglePetType(pt);
                                  }}
                                >
                                  <div
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      isSelected
                                        ? "bg-blue-600 border-blue-600 text-white"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {isSelected && "✓"}
                                  </div>
                                  <span>{pt.name}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  </FormField>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Environment">
                  <div className="flex gap-6 mt-1">
                    {["Cage", "Cage Free"].map((env) => (
                      <label key={env} className="flex items-center gap-2">
                        <input
                          type="radio"
                          value={env}
                          checked={roomForm.environment_type === env}
                          onChange={() => handleChange("environment_type", env)}
                          className="text-blue-600"
                        />
                        {env}
                      </label>
                    ))}
                  </div>
                </FormField>

                <FormField label="Camera">
                  <div className="flex gap-6 mt-1">
                    {["Yes", "No"].map((opt) => (
                      <label key={opt} className="flex items-center gap-2">
                        <input
                          type="radio"
                          value={opt}
                          checked={roomForm.camera === opt}
                          onChange={() => handleChange("camera", opt)}
                          className="text-blue-600"
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  label="Occupancy Limit"
                  classApplied="col-span-1 md:col-span-2"
                >
                  <input
                    type="number"
                    min={1}
                    value={roomForm.occupancy_limit}
                    onChange={(e) =>
                      handleChange("occupancy_limit", e.target.value)
                    }
                    className="border border-gray-300 rounded-md w-full p-2 focus:ring-1 focus:ring-blue-500"
                  />
                </FormField>
              </div>

              <FormField label="Set Default Numbering Start with 101">
                <div className="flex gap-6 mt-1">
                  {["Yes", "No"].map((opt) => (
                    <label key={opt} className="flex items-center gap-2">
                      <input
                        type="radio"
                        value={opt}
                        checked={roomForm.set_default_numbering === opt}
                        onChange={() =>
                          handleChange("set_default_numbering", opt)
                        }
                        className="text-blue-600"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </FormField>

              {roomForm.set_default_numbering === "No" && (
                <div>
                  <h3 className="font-semibold text-lg mb-2">
                    Room Numbers <span className="text-red-500">*</span>
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {roomBoxes.map((box) => (
                      <div key={box.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Room {box.id} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={box.roomNumber}
                          onChange={(e) =>
                            handleBoxChange(box.id, e.target.value)
                          }
                          placeholder="Enter number"
                          className="border border-gray-300 rounded-md w-full p-2 focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <FormField label="Description">
                <textarea
                  value={roomForm.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Enter a detailed description..."
                  className="border border-gray-300 rounded-md w-full p-2 h-28 resize-none focus:ring-1 focus:ring-blue-500"
                />
              </FormField>

              <FileUpload
                uploadEndpoint="/upload/fileUpload"
                acceptedTypes={["image/png", "image/jpeg", "image/gif"]}
                multiple
                maxFiles={10}
                moduleName="vendors"
                docType="room"
                existingUrls={roomForm.images}
                onUploadComplete={(urls) => {
                  const imageUrls = urls.map((url) => url);
                  setRoomForm((prev) => ({ ...prev, images: imageUrls }));
                }}
                editMode={!!roomId}
              />
            </>
          )}
        </div>

        <div className="p-3 border-t bg-white flex gap-3">
          <button
            onClick={onClose}
            className="w-1/2 py-2.5 border border-[#F2C043] text-gray-800 rounded-md hover:bg-gray-50 transition"
            style={{ backgroundColor: "#FFFEF8" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSaveRoom}
            disabled={isSaving || isCheckingBookings}
            className="w-1/2 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Room"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoomTypeDrawer;
